import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  Grid,
  Card,
  Collapse,
  IconButton,
} from '@mui/material';
import { ExpandMore, CheckCircle, Build, CloudUpload, Security, Assessment, SupportAgent, MailOutline, SettingsEthernet } from '@mui/icons-material';

// Placeholder SVG for DevOps pipeline
const DevOpsSVG = () => (
  <svg width="100%" height="80" viewBox="0 0 600 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="20" y="20" width="120" height="40" rx="20" fill="#0B60B1" />
    <text x="80" y="48" fill="#fff" fontSize="20" fontWeight="bold" textAnchor="middle">Plan</text>
    <rect x="170" y="20" width="120" height="40" rx="20" fill="#2196F3" />
    <text x="230" y="48" fill="#fff" fontSize="20" fontWeight="bold" textAnchor="middle">Deploy</text>
    <rect x="320" y="20" width="120" height="40" rx="20" fill="#0B60B1" />
    <text x="380" y="48" fill="#fff" fontSize="20" fontWeight="bold" textAnchor="middle">Test</text>
    <rect x="470" y="20" width="120" height="40" rx="20" fill="#2196F3" />
    <text x="530" y="48" fill="#fff" fontSize="20" fontWeight="bold" textAnchor="middle">Production</text>
    <polygon points="140,40 170,40 170,45 140,45" fill="#fff" />
    <polygon points="290,40 320,40 320,45 290,45" fill="#fff" />
    <polygon points="440,40 470,40 470,45 440,45" fill="#fff" />
  </svg>
);

const devOpsPhases = [
  {
    title: 'Plan',
    icon: <Build sx={{ fontSize: 40, color: 'primary.main' }} />,
    desc: 'Consult, architect, and design your Postfix deployment for your business needs.'
  },
  {
    title: 'Deploy',
    icon: <CloudUpload sx={{ fontSize: 40, color: 'primary.main' }} />,
    desc: 'We install, configure, and integrate Postfix with your infrastructure.'
  },
  {
    title: 'Test',
    icon: <Security sx={{ fontSize: 40, color: 'primary.main' }} />,
    desc: 'Deliverability, security, and compliance testing for optimal results.'
  },
  {
    title: 'Production',
    icon: <Assessment sx={{ fontSize: 40, color: 'primary.main' }} />,
    desc: 'Scale, monitor, and support your Postfix instance in production.'
  }
];

const features = [
  { icon: <MailOutline sx={{ fontSize: 32, color: 'primary.main' }} />, title: 'Email Server', desc: 'Robust and reliable email server for handling high volumes.' },
  { icon: <Security sx={{ fontSize: 32, color: 'primary.main' }} />, title: 'Security', desc: 'Advanced security features and spam protection.' },
  { icon: <SettingsEthernet sx={{ fontSize: 32, color: 'primary.main' }} />, title: 'Customization', desc: 'Highly configurable for your specific needs.' },
  { icon: <SupportAgent sx={{ fontSize: 32, color: 'primary.main' }} />, title: 'Expert Support', desc: '24/7 support and proactive monitoring.' },
];

const faqs = [
  {
    q: 'What is Postfix?',
    a: 'Postfix is a free and open-source mail transfer agent (MTA) that routes and delivers electronic mail, designed as an alternative to the widely used Sendmail MTA.'
  },
  {
    q: 'How long does setup take?',
    a: 'A typical Postfix deployment takes 2-4 days, depending on your requirements and infrastructure complexity.'
  },
  {
    q: 'Do you provide ongoing support?',
    a: 'Yes, we offer 24/7 support, monitoring, and deliverability consulting for your Postfix server.'
  },
  {
    q: 'Can you migrate my existing email?',
    a: 'Absolutely! We handle migration from your current email server to Postfix.'
  }
];

export default function PostfixPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* HERO */}
      <Box sx={{ bgcolor: 'primary.main', color: '#fff', py: { xs: 8, md: 12 }, textAlign: 'center', mb: 6 }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={800} gutterBottom>Postfix Consulting: Plan, Deploy, Test, Deliver</Typography>
          <Typography variant="h5" sx={{ mb: 4, fontWeight: 400, color: 'rgba(255,255,255,0.85)' }}>
            End-to-end Postfix consulting for reliable, secure, and scalable email delivery.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 4 }}>
            <Button variant="contained" color="secondary" size="large" href="/contact">Get Started</Button>
            <Button variant="outlined" color="inherit" size="large">Book a Free Consultation</Button>
          </Stack>
          <Box sx={{ mt: 4 }}>
            <DevOpsSVG />
          </Box>
        </Container>
      </Box>

      {/* DEVOPS PHASES */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Grid container spacing={4} justifyContent="center">
          {devOpsPhases.map((phase) => (
            <Grid key={phase.title} item xs={12} sm={6} md={3}>
              <Paper elevation={0} sx={{ p: 4, borderRadius: 3, textAlign: 'center', bgcolor: '#fff', boxShadow: '0 2px 12px rgba(11,96,177,0.06)' }}>
                {phase.icon}
                <Typography fontWeight={700} sx={{ mt: 2, mb: 1 }}>{phase.title}</Typography>
                <Typography variant="body2" color="text.secondary">{phase.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FEATURES */}
      <Box sx={{ bgcolor: '#fff', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700} sx={{ mb: 6, textAlign: 'center', color: 'primary.main' }}>Postfix Features</Typography>
          <Grid container spacing={4} justifyContent="center">
            {features.map((feature) => (
              <Grid key={feature.title} item xs={12} sm={6} md={3}>
                <Card elevation={0} sx={{ p: 3, borderRadius: 3, textAlign: 'center', bgcolor: '#f8fafc', boxShadow: '0 2px 12px rgba(11,96,177,0.04)' }}>
                  {feature.icon}
                  <Typography fontWeight={700} sx={{ mt: 2, mb: 1 }}>{feature.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{feature.desc}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* WHY DO YOU NEED POSTFIX */}
      <Container maxWidth="md" sx={{ py: 10 }}>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 4, color: 'primary.main', textAlign: 'center' }}>Why Do You Need Postfix?</Typography>
        <Stack spacing={3}>
          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircle color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="body1" fontWeight={500}>Enterprise-grade email server with proven reliability and security.</Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircle color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="body1" fontWeight={500}>Full control over your email infrastructure and data privacy.</Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircle color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="body1" fontWeight={500}>Highly scalable and customizable for your specific needs.</Typography>
          </Paper>
        </Stack>
      </Container>

      {/* HOW CAN WE START */}
      <Box sx={{ bgcolor: '#fff', py: 10 }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={700} sx={{ mb: 4, color: 'primary.main', textAlign: 'center' }}>How Can We Start?</Typography>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ p: 4, borderRadius: 3, textAlign: 'center', bgcolor: '#f8fafc' }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>1. Book a Call</Typography>
                <Typography variant="body2" color="text.secondary">Schedule a free consultation to discuss your Postfix needs.</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ p: 4, borderRadius: 3, textAlign: 'center', bgcolor: '#f8fafc' }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>2. Get a Custom Plan</Typography>
                <Typography variant="body2" color="text.secondary">Receive a tailored Postfix deployment and migration plan.</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ p: 4, borderRadius: 3, textAlign: 'center', bgcolor: '#f8fafc' }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>3. Go Live</Typography>
                <Typography variant="body2" color="text.secondary">We deploy, test, and support your Postfix server.</Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ */}
      <Container maxWidth="md" sx={{ py: 10 }}>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 4, color: 'primary.main', textAlign: 'center' }}>Frequently Asked Questions</Typography>
        <Stack spacing={3}>
          {faqs.map((faq, idx) => (
            <Paper key={faq.q} elevation={0} sx={{ p: 4, borderRadius: 3, bgcolor: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography fontWeight={600}>{faq.q}</Typography>
                <IconButton onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}>
                  <ExpandMore sx={{ transform: openFAQ === idx ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }} />
                </IconButton>
              </Box>
              <Collapse in={openFAQ === idx}>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{faq.a}</Typography>
              </Collapse>
            </Paper>
          ))}
        </Stack>
      </Container>

      {/* CTA */}
      <Box sx={{ bgcolor: 'primary.main', color: '#fff', py: 8, textAlign: 'center', mt: 8 }}>
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>Ready to Modernize Your Email Server?</Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'rgba(255,255,255,0.85)' }}>
            Let's architect, deploy, and support your Postfix infrastructure.
          </Typography>
          <Button variant="contained" color="secondary" size="large" href="/contact">Start Your Project</Button>
        </Container>
      </Box>
    </Box>
  );
} 